import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUserProfileTags } from '../../api/profileTagAdminReads';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { API_ENV_TEST, API_ENV_PROD } from '../../utils/apiEnvironment';
import useUserDetailScope from '../../hooks/useUserDetailScope';

const selectionGroups = [
  ['nationality', '국적'], ['gender', '성별'], ['relationship', '관계'],
  ['mbti', 'MBTI'], ['zodiac', '별자리'],
];
const listGroups = [
  ['interests', '선택한 관심사'], ['custom_interests', '직접 입력 관심사'],
  ['share_topics', '나누고 싶은 것'], ['conversation_styles', '좋아하는 대화 방식'],
];

function textOf(value) {
  return value?.text || value?.id || '값 없음';
}

function Visibility({ visible }) {
  return <span className={`rounded px-2 py-0.5 text-xs ${visible ? 'bg-emerald-500/10 text-emerald-200' : 'bg-amber-500/10 text-amber-200'}`}>{visible ? '공개' : '비공개'}</span>;
}

function Selection({ value }) {
  if (!value) return <span className="text-dark-text-secondary">선택 없음</span>;
  return <span className="flex flex-wrap items-center gap-2"><span className="break-all">{textOf(value)}</span><Visibility visible={value.visible} /></span>;
}

function List({ items }) {
  if (!items?.length) return <p className="text-dark-text-secondary">선택 없음</p>;
  return <ul className="flex flex-wrap gap-2">{items.map((item, index) => <li key={`${item.id || item.text || 'empty'}-${index}`} className="flex max-w-full items-center gap-2 rounded border border-dark-border bg-dark-bg px-2 py-1"><span className="break-all">{textOf(item)}</span><Visibility visible={item.visible} /></li>)}</ul>;
}

function ErrorState({ error, onRetry }) {
  const message = error?.status === 403 ? '프로필 태그 조회 권한이 없습니다.'
    : error?.status === 503 || error?.status === 404 ? '현재 환경에서 프로필 태그 조회가 준비되지 않았습니다.'
      : '프로필 태그를 불러오지 못했습니다.';
  return <p role="alert" className="text-sm text-amber-200">{message} <button type="button" onClick={onRetry} className="underline">다시 시도</button></p>;
}

export default function ProfileTagDetail(props) {
  const { apiEnvironment } = useApiEnvironment();
  const { cacheSession, role } = useAuth();
  if (![API_ENV_TEST, API_ENV_PROD].includes(apiEnvironment)) {
    return <section aria-label="프로필 태그 상세"><p role="status">프로필 태그를 조회할 TEST 또는 PROD 환경을 선택해 주세요.</p></section>;
  }
  return <ScopedProfileTagDetail key={`${apiEnvironment}:${cacheSession}:${role}`} {...props} apiEnvironment={apiEnvironment} />;
}

function ScopedProfileTagDetail({ userId, token, apiEnvironment, onUnauthorized }) {
  const { role } = useAuth();
  const { scope, authenticated } = useUserDetailScope('profile-tag-detail', apiEnvironment, token);
  const canRead = role === 'admin' && authenticated && !!userId;
  const query = useQuery({
    queryKey: [...scope, 'profileTags', userId],
    queryFn: () => getUserProfileTags(userId, token, apiEnvironment),
    enabled: canRead,
    retry: false,
  });
  React.useEffect(() => { if (query.error?.status === 401) onUnauthorized?.(); }, [query.error, onUnauthorized]);
  const data = canRead ? query.data : null;
  const fields = data?.fields || {};

  return <section aria-label="프로필 태그 상세" className="space-y-4 text-sm">
    <p className="text-xs text-dark-text-secondary">{String(apiEnvironment).toUpperCase()} · 저장된 선택과 공개 설정을 관리자에게만 표시합니다. 조회 목적은 감사 기록에 남습니다. 개별 태그 삭제·숨김은 제공하지 않습니다.</p>
    {!canRead && <p role="alert">활성 Admin 로그인으로만 프로필 태그를 볼 수 있습니다.</p>}
    {canRead && query.isLoading && <p>프로필 태그 불러오는 중…</p>}
    {canRead && query.error && <ErrorState error={query.error} onRetry={() => query.refetch()} />}
    {data && <>
      <p className="text-xs text-dark-text-secondary">{data.updated_at ? `프로필 최종 수정: ${new Date(data.updated_at).toLocaleString()}` : '아직 저장한 프로필 태그가 없습니다.'} · revision {data.revision ?? 0} · catalog {data.catalog_version || '없음'}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {selectionGroups.map(([key, label]) => <div key={key} className="rounded border border-dark-border bg-dark-surface p-3"><h3 className="mb-2 font-semibold">{label}</h3><Selection value={fields[key]} /></div>)}
        <div className="rounded border border-dark-border bg-dark-surface p-3"><h3 className="mb-2 font-semibold">나이 공개</h3><Visibility visible={fields.age_visible} /></div>
      </div>
      {listGroups.map(([key, label]) => <div key={key} className="rounded border border-dark-border bg-dark-surface p-3"><h3 className="mb-2 font-semibold">{label} ({fields[key]?.length || 0})</h3><List items={fields[key]} /></div>)}
      <div className="rounded border border-dark-border bg-dark-surface p-3"><h3 className="mb-2 font-semibold">관심사 저장 순서</h3>{fields.interest_order?.length ? <ol className="list-inside list-decimal break-all">{fields.interest_order.map((item, index) => <li key={`${item.id || item.text}-${index}`}>{textOf(item)}</li>)}</ol> : <p className="text-dark-text-secondary">없음</p>}</div>
      <div className="rounded border border-dark-border bg-dark-surface p-3"><h3 className="mb-2 font-semibold">대표 태그 ({data.pins?.length || 0})</h3>{data.pins?.length ? <ol className="list-inside list-decimal break-all">{data.pins.map((pin, index) => <li key={`${pin.kind}-${pin.id || pin.text}-${index}`}>{pin.kind} · {textOf(pin)}</li>)}</ol> : <p className="text-dark-text-secondary">없음</p>}</div>
    </>}
  </section>;
}
